<template>
  <m-pageframe title="{organization-label}: Guest Check In Forms">
    <v-container class="mb-10">
      <m-beta-alert code="beta-guest-check-in" />

      <card-grid-with-controls :show_loading_state="loading">
        <template #header>
          <h1 class="text-h4 font-weight-bold mb-1">Guest Check-In Forms</h1>
          <p class="text-body-2 text--secondary mb-0">
            Engage with your guests and collect valuable information
          </p>
        </template>

        <template #header-actions>
          <v-btn
            color="primary"
            :disabled="loading"
            @click="onClickAddGuestCheckIn"
          >
            <v-icon class="mr-1">mdi-plus</v-icon>
            Create Guest Check-In Form
          </v-btn>

          <v-menu
            bottom
            offset-y
            left
            :disabled="loading"
          >
            <template v-slot:activator="{ on }">
              <v-btn
                icon
                v-on="on"
              >
                <v-icon>mdi-dots-vertical</v-icon>
              </v-btn>
            </template>

            <v-list
              class="py-0"
              dense
            >
              <v-subheader>ANALYTICS & SUBMISSIONS</v-subheader>
              <v-list-item @click="viewGuestCheckInAnalytics">
                <v-list-item-icon><v-icon>mdi-chart-line</v-icon></v-list-item-icon>
                <v-list-item-title>All Guest Check-In Analytics</v-list-item-title>
              </v-list-item>
              <v-list-item @click="viewGuestCheckInSubmissions">
                <v-list-item-icon><v-icon>mdi-clipboard-list</v-icon></v-list-item-icon>
                <v-list-item-title>All Guest Check-In Submissions</v-list-item-title>
              </v-list-item>

              <v-divider />

              <v-subheader>HOW IT WORKS</v-subheader>

              <v-list-item @click="viewDemoGuestCheckInFlow">
                <v-list-item-icon><v-icon>mdi-presentation</v-icon></v-list-item-icon>
                <v-list-item-title>Demo - Guest Check-In Forms</v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>
        </template>

        <template #filter>
          <div class="d-flex align-center gap-4">
            <v-btn-toggle
              v-model="filter_status"
              dense
              mandatory
            >
              <v-btn value="all">All ({{ num_all }})</v-btn>
              <v-btn value="published">Published ({{ num_published }})</v-btn>
              <v-btn value="draft">Drafts ({{ num_drafts }})</v-btn>
            </v-btn-toggle>

            <v-checkbox
              v-model="show_archived"
              label="Show Archived"
              :disabled="loading"
              class="mt-0 pt-0"
              hide-details
            />
          </div>
        </template>

        <template #search>
          <v-text-field
            label="Search"
            prepend-inner-icon="mdi-magnify"
            autocomplete="off"
            :disabled="loading"
            v-model="full_text"
            dense
            hide-details
            clearable
            outlined
          />
        </template>

        <template #grid>
          <v-col
            v-for="item in results"
            :key="item.guest_check_in_form_id"
            cols="12"
            md="6"
            lg="4"
          >
            <card-with-action-header
              :header_text="item.internal_label"
              :menu_items="getMenuItems(item)"
              :menu_aria_label="`Actions for ${item.internal_label} form`"
              :aria_label="`Edit form: ${item.internal_label}`"
              :is_archived="item.is_archived"
              :is_draft="isItemDraft(item)"
              interactive
              @click="onClickEdit(item)"
              @menu-action="onMenuAction"
            >
              <div class="pa-4">
                <div class="text--secondary mb-3 d-flex justify-space-between align-center">
                  <div class="d-flex align-center gap-1">
                    <v-icon
                      small
                      color="primary"
                      class="mr-1"
                    >mdi-clipboard-list-outline</v-icon>
                    Submissions:
                  </div>
                  <strong class="ml-1">{{ item.total_submissions || 0 }}</strong>
                </div>

                <div class="text--secondary d-flex justify-space-between align-center">
                  <div class="d-flex align-center">
                    <v-icon
                      small
                      :color="item.is_published ? 'success' : 'primary'"
                      class="mr-2"
                    >
                      {{ item.is_published ? 'mdi-check-circle' : 'mdi-pencil' }}
                    </v-icon>
                    Published:
                  </div>
                  <strong class="ml-1">{{ item.is_published ? 'Yes' : 'No' }}</strong>
                </div>

                <div class="text--secondary d-flex justify-space-between align-center mt-3">
                  <div class="d-flex align-center">
                    <v-icon
                      small
                      color="primary"
                      class="mr-2"
                    >mdi-clock-outline</v-icon>
                    Last Published:
                  </div>
                  <strong
                    v-if="item.is_published && item.publish_time"
                    class="ml-1"
                  >
                    {{ $rc.pretty(item.publish_time, 'date_and_time') }}
                  </strong>
                  <strong
                    v-else
                    class="ml-1"
                  >-</strong>
                </div>

                <div class="text--secondary d-flex justify-space-between align-center mt-3">
                  <div class="d-flex align-center">
                    <v-icon
                      small
                      color="primary"
                      class="mr-2"
                    >mdi-account</v-icon>
                    Last Published By:
                  </div>
                  <strong
                    v-if="item.is_published && item.publish_by_employee_label"
                    class="ml-1"
                  >
                    {{ item.publish_by_employee_label }}
                  </strong>
                  <strong
                    v-else
                    class="ml-1"
                  >-</strong>
                </div>
              </div>
            </card-with-action-header>
          </v-col>
        </template>

        <template #empty-state>
          <empty-state
            icon="mdi-package-variant"
            title="No Items Found"
            :description="is_creating_very_first_form
              ? 'Create your first guest check-in form to get started!'
              : 'Try adjusting your search or filter criteria'
              "
          >
            <template #actions>
              <div v-if="has_none_published_with_drafts">
                <v-btn
                  color="primary"
                  outlined
                  @click="filter_status = 'draft'"
                >
                  View Drafts ({{ num_drafts }})
                </v-btn>
              </div>
            </template>
          </empty-state>
        </template>

        <template #additional-content>
          <qr-code-dialog ref="qrCodeDialog" />
        </template>
      </card-grid-with-controls>
    </v-container>
  </m-pageframe>
</template>

<script>
import CardGridWithControls from '@/components/layouts/card-grid-with-controls.vue';
import CardWithActionHeader from '@/components/shared/cards/card-with-action-header.vue';
import _ from 'lodash';

export default {
  name: 'SearchFormsGuestCheckIn',

  components: {
    CardGridWithControls,
    CardWithActionHeader,
    EmptyState: () => import('@/components/global/empty-state.vue'),
    QrCodeDialog: () => import('@/components/input/qr-code-dialog.vue'),
  },

  data() {
    return {
      all_forms: [],
      full_text: null,
      filter_status: 'all', // 'all', 'published', 'draft'
      show_archived: false,
      loading: false,
    };
  },

  mounted() {
    this.doUpdate();
  },

  methods: {
    update: _.debounce(function () {
      this.doUpdate();
    }, 500),

    doUpdate() {
      this.loading = true;

      const params = {
        organization_id: this.$route.params.organization_id,
      };

      this.$rc.rest({
        spinner: false,
        endpoint: 'POST: /api/v1/guest-check-in/forms/search',
        params,
        on_success: data => {
          // move archived to the end
          this.all_forms = data.results?.sort((a, b) => {
            if (a.is_archived === b.is_archived) return 0;
            if (a.is_archived) return 1;
            return -1;
          });

          this.loading = false;
        },
        on_error: 'Unable to load guest check-in forms',
        on_finally: () => {
          this.loading = false;
        },
      });
    },

    isItemDraft(item) {
      return !item.is_archived && !item.is_published;
    },

    onClickEdit(item) {
      this.$router.push({
        name: 'guest-check-in-edit',
        params: {
          organization_id: this.$route.params.organization_id,
          guest_check_in_form_id: item.guest_check_in_form_id,
        },
      });
    },

    getMenuItems(item) {
      const menu_items = [
        {
          text: 'Edit',
          action: 'edit',
          icon: 'mdi-pencil',
          item,
        },

        { divider: true },
        {
          text: item.is_archived ? 'Unarchive' : 'Archive',
          action: 'toggle-archive',
          icon: item.is_archived ? 'mdi-package-up' : 'mdi-archive',
          item,
        },
      ];

      if (item.is_published) {
        menu_items.push(
          { divider: true },
          {
            text: 'Copy Form URL',
            action: 'copy-form-url',
            icon: 'mdi-content-copy',
            item,
          },
          {
            text: 'Generate QR Code',
            action: 'generate-qr-code',
            icon: 'mdi-qrcode',
            item,
          },
          { divider: true },
          {
            text: 'View Analytics',
            action: 'view-analytics',
            icon: 'mdi-chart-line',
            item,
          },
          {
            text: 'View Submissions',
            action: 'view-submissions',
            icon: 'mdi-clipboard-list',
            item,
          }
        );
      } else {
        menu_items.push(
          { divider: true },
          {
            text: 'View Submissions',
            action: 'view-submissions',
            icon: 'mdi-clipboard-list',
            item,
          }
        );
      }

      if (item.is_published && !item.is_archived) {
        menu_items.push(
          { divider: true },
          {
            text: 'Duplicate',
            action: 'duplicate',
            icon: 'mdi-content-copy',
            item,
          }
        );
      }

      return menu_items;
    },

    onMenuAction(action, item) {
      const { item: form } = item;

      switch (action) {
        case 'edit':
          this.onClickEdit(form);
          break;
        case 'view-analytics':
          this.onClickViewAnalytics(form);
          break;
        case 'view-submissions':
          this.onClickViewSubmissions(form);
          break;
        case 'copy-form-url':
          this.onClickCopyURL(form);
          break;
        case 'generate-qr-code':
          this.onClickGenerateQRCode(form);
          break;
        case 'toggle-archive':
          this.onClickToggleArchive(form);
          break;
        case 'duplicate':
          this.onClickDuplicate(form);
          break;
      }
    },

    onClickToggleArchive(item) {
      const action = item.is_archived ? 'Unarchive' : 'Archive';
      this.$rc.confirm({
        title: `${action} Form`,
        body: `Are you sure you want to ${action.toLowerCase()} the form: "${item.internal_label
          }"?`,
        btn_text_ok: action,
        btn_text_cancel: 'Cancel',
        on_ok: () => this.doToggleArchived(item),
      });
    },

    onClickDuplicate(item) {
      this.$rc.confirm({
        title: 'Duplicate Form',
        body: 'Are you sure you want to duplicate the form: ' + item.internal_label,
        btn_text_ok: 'Duplicate',
        btn_text_cancel: 'Cancel',
        on_ok: () => this.doDuplicate(item),
      });
    },

    doDuplicate(item) {
      const form_id = item.guest_check_in_form_id;
      this.$rc.rest({
        spinner: true,
        endpoint: 'PUT: /api/v1/guest-check-in/forms/duplicate',
        params: { guest_check_in_form_id: form_id },
        on_success: () => {
          this.doUpdate();
        },
        on_error: 'Unable to duplicate form',
      });
    },

    doToggleArchived(item) {
      const form_id = item.guest_check_in_form_id;
      const archived = !item.is_archived;
      this.$rc.rest({
        spinner: true,
        endpoint: 'PATCH: /api/v1/guest-check-in/forms/:guest_check_in_form_id/archive',
        params: { guest_check_in_form_id: form_id, archived: archived },
        on_success: () => {
          this.doUpdate();
        },
        on_error: 'Unable to update form status',
      });
    },

    onClickAddGuestCheckIn() {
      this.$router.push({
        name: 'guest-check-in-template-selection',
        params: {
          organization_id: this.$route.params.organization_id,
        },
      });
    },

    viewGuestCheckInAnalytics() {
      this.$router.push({
        name: 'guest-check-in-analytics',
        params: {
          organization_id: this.$route.params.organization_id,
        },
      });
    },

    viewGuestCheckInSubmissions() {
      this.$router.push({
        name: 'guest-check-in-submissions',
        params: {
          organization_id: this.$route.params.organization_id,
        },
      });
    },

    viewDemoGuestCheckInFlow() {
      const route = this.$router.resolve({
        name: 'search-forms-guest-check-in-demo-gateway',
      });
      window.open(route.href, '_blank');
    },

    onClickViewAnalytics(item) {
      this.$router.push({
        name: 'guest-check-in-analytics',
        params: {
          organization_id: this.$route.params.organization_id,
        },
        query: {
          guest_check_in_form_id: item.guest_check_in_form_id,
        },
      });
    },

    onClickViewSubmissions(item) {
      this.$router.push({
        name: 'guest-check-in-submissions',
        params: {
          organization_id: this.$route.params.organization_id,
        },
        query: {
          guest_check_in_form_id: item.guest_check_in_form_id,
        },
      });
    },

    onClickCopyURL(item) {
      const public_url = `${window.location.origin}/app/guest-check-in/${item.guest_check_in_form_id}`;
      this.$rc.copy(public_url);
    },

    onClickGenerateQRCode(item) {
      this.$rc.rest({
        spinner: true,
        endpoint: 'PUT: /api/v1/guest-check-in/forms/generate-qr-code',
        params: {
          guest_check_in_form_id: item.guest_check_in_form_id,
        },
        on_success: data => {
          this.$refs.qrCodeDialog.showDialog(data.qr_code_id);
        },
        on_error: 'Unable to generate QR code',
      });
    },

    formatPublishTime(timestamp) {
      if (!timestamp) return '';
      const date = new Date(timestamp);
      return date.toLocaleDateString();
    },
  },

  computed: {
    full_text_sanitized() {
      return (this.full_text || '').trim().toLowerCase();
    },

    results() {
      if (!this.all_forms) return [];

      // front-end filtering so we have access to the number of items under each filter
      return this.all_forms.filter(item => {
        const matches_text = item.internal_label.toLowerCase().includes(this.full_text_sanitized);

        if (!matches_text) return false;

        if (!this.show_archived && item.is_archived) {
          return false;
        }

        if (this.filter_status === 'all') {
          return true;
        } else if (this.filter_status === 'published') {
          return item.is_published;
        } else if (this.filter_status === 'draft') {
          return !item.is_published;
        }

        return true;
      });
    },

    num_drafts() {
      return this.all_forms.filter(item => {
        if (!this.show_archived && item.is_archived) return false;
        return !item.is_published;
      }).length;
    },

    num_published() {
      return this.all_forms.filter(item => {
        if (!this.show_archived && item.is_archived) return false;
        return item.is_published;
      }).length;
    },

    num_archived() {
      return this.all_forms.filter(item => item.is_archived).length;
    },

    num_all() {
      return this.all_forms.filter(item => {
        if (!this.show_archived && item.is_archived) return false;
        return true;
      }).length;
    },

    is_creating_very_first_form() {
      return this.all_forms.length === 0;
    },

    has_none_published_with_drafts() {
      return this.filter_status === 'published' && this.num_drafts > 0;
    },
  },
};
</script>

<style lang="scss" scoped>
.container {
  max-width: 84rem;
}
</style>
